/**
 * ============================================================================
 * RHION OUR STORY — MASTER UNIFIED SCROLLYTELLING ANIMATION ENGINE
 * 100% Continuous Single Pinned Canvas (Phase 1 + Phase 2 + Phase 3)
 * Powered by GSAP & ScrollTrigger
 * ============================================================================
 */

(() => {
  'use strict';

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const ensureLenis = async () => {
    if (window.Lenis) return window.Lenis;
    await loadScript('https://unpkg.com/lenis@1.1.18/dist/lenis.min.js');
    return window.Lenis;
  };

  const ensureGsap = async () => {
    if (window.gsap && window.ScrollTrigger) {
      return { gsap: window.gsap, ScrollTrigger: window.ScrollTrigger };
    }

    if (!window.gsap) {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js');
    }
    if (!window.ScrollTrigger) {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js');
    }

    window.gsap.registerPlugin(window.ScrollTrigger);
    return { gsap: window.gsap, ScrollTrigger: window.ScrollTrigger };
  };

  const initLenisSmoothScroll = async () => {
    if (window._rhionLenis) return window._rhionLenis;
    try {
      const LenisClass = await ensureLenis();
      if (!LenisClass) return null;

      const lenis = new LenisClass({
        duration: 1.6, // Luxurious smooth momentum
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Smooth exponential ease
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 0.85, // Calms fast mouse wheel flicks
        touchMultiplier: 1.3, // Soft touch glide on mobile
        infinite: false,
      });

      window._rhionLenis = lenis;

      if (window.ScrollTrigger) {
        lenis.on('scroll', window.ScrollTrigger.update);
      }

      if (window.gsap) {
        window.gsap.ticker.add((time) => {
          lenis.raf(time * 1000);
        });
        window.gsap.ticker.lagSmoothing(0);
      }

      return lenis;
    } catch (e) {
      console.warn('[Rhion Story Master] Lenis smooth scroll fallback:', e);
      return null;
    }
  };

  const initMasterStory = (section) => {
    if (!section || section.dataset.masterInitialized === 'true') return;
    section.dataset.masterInitialized = 'true';

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    // --- Phase 1 DOM Elements ---
    const p1LogoWrap = section.querySelector('[data-p1-logo-wrap]');
    const p1LogoSvgWrap = section.querySelector('[data-p1-logo-svg-wrap]');
    const p1LogoSvg = section.querySelector('[data-p1-logo-svg]');
    const p1LogoOthers = section.querySelector('[data-p1-logo-others]');
    const p1SvgLetterO = section.querySelector('[data-p1-svg-letter-o]');
    const p1MarqueeLayer = section.querySelector('[data-p1-marquee-layer]');
    const p1MarqueeTrack = section.querySelector('[data-p1-marquee-track]');
    const p1HandWrap = section.querySelector('[data-p1-hand-wrap]');
    const p1CalloutTop = section.querySelector('[data-p1-callout-top]');
    const p1CalloutBottom = section.querySelector('[data-p1-callout-bottom]');

    // --- Phase 2 DOM Elements ---
    const p2Container = section.querySelector('[data-p2-container]');
    const p2HeadingWrap = section.querySelector('[data-p2-heading-wrap]');
    const p2Lockup = section.querySelector('[data-p2-lockup]');
    const p2Wordmark = section.querySelector('[data-p2-wordmark]');
    const p2EmblemWrap = section.querySelector('[data-p2-emblem-wrap]');
    const p2EmblemScaler = section.querySelector('[data-p2-emblem-scaler]');
    const p2LogomarkSvg = section.querySelector('[data-p2-logomark-svg]');
    const p2Swirls = section.querySelectorAll('[data-p2-swirl]');
    const p2ElementSvgWrap = section.querySelector('[data-p2-element-svg-wrap]');
    const p2ElementRotator = section.querySelector('[data-p2-element-rotator]');
    const p2ElementHeadingWrap = section.querySelector('[data-p2-element-heading-wrap]');
    const p2LensWrap = section.querySelector('[data-p2-lens-wrap]');

    // --- Phase 3 DOM Elements ---
    const p3TechnicalWrap = section.querySelector('[data-p3-technical-wrap]');
    const p3IntelligentWrap = section.querySelector('[data-p3-intelligent-wrap]');
    const p3ShapeWrap = section.querySelector('[data-p3-shape-wrap]');
    const p3ShapeImg = section.querySelector('[data-p3-shape-img]');
    const p3FrameCanvas = section.querySelector('[data-p3-frame-canvas]');

    // --- Phase 4 DOM Elements (Hold Frame Sequential Texts) ---
    const holdTextTop = section.querySelector('[data-hold-text-top]');
    const holdTextBottom = section.querySelector('[data-hold-text-bottom]');
    const holdBulbLeft = section.querySelector('[data-hold-bulb-left]');
    const holdBulbRight = section.querySelector('[data-hold-bulb-right]');

    // --- Phase 5 DOM Elements (5 Showcase Slides) ---
    const p5ImageWrap1 = section.querySelector('[data-p5-image-wrap="1"]');
    const p5CenterWrap1 = section.querySelector('[data-p5-center-wrap="1"]');

    const p5ImageWrap2 = section.querySelector('[data-p5-image-wrap="2"]');
    const p5CenterWrap2 = section.querySelector('[data-p5-center-wrap="2"]');

    const p5ImageWrap3 = section.querySelector('[data-p5-image-wrap="3"]');
    const p5CenterWrap3 = section.querySelector('[data-p5-center-wrap="3"]');

    const p5ImageWrap4 = section.querySelector('[data-p5-image-wrap="4"]');
    const p5CenterWrap4 = section.querySelector('[data-p5-center-wrap="4"]');

    const p5ImageWrap5 = section.querySelector('[data-p5-image-wrap="5"]');
    const p5CenterWrap5 = section.querySelector('[data-p5-center-wrap="5"]');

    const p5OutlineWrap = section.querySelector('[data-p5-outline-wrap]');

    // --- Frame Animation Config (Desktop 526->602, Mobile 421->504) ---
    const isMobileDevice = window.innerWidth <= 768;
    const FRAME_START = isMobileDevice ? 421 : 526;
    const FRAME_END = isMobileDevice ? 504 : 602;
    const TOTAL_FRAMES = FRAME_END - FRAME_START;
    const FRAME_BASE_URL = p3FrameCanvas ? (p3FrameCanvas.dataset.frameBaseUrl || '') : '';

    // Build frame URL based on device viewport
    const resolveFrameUrl = (frameNum) => {
      if (isMobileDevice) {
        return `${FRAME_BASE_URL}ezgif-frame-mob-${frameNum}.jpg`;
      }
      return `${FRAME_BASE_URL}ezgif-frame-${frameNum}.jpg`;
    };

    // Preload frames into Image objects
    const frameImages = [];
    let framesLoadedCount = 0;
    let canvasActivated = false; // tracks if canvas crossfade has happened

    const preloadFrames = () => {
      for (let i = FRAME_START; i <= FRAME_END; i++) {
        const img = new Image();
        img.src = resolveFrameUrl(i);
        const frameIdx = i - FRAME_START;
        img.onload = () => {
          framesLoadedCount++;
          if (frameIdx === 0) {
            drawFrame(0);
          }
        };
        img.onerror = () => {
          console.warn(`[Rhion] Frame ${i} failed to load`);
        };
        frameImages.push(img);
      }
    };

    if (FRAME_BASE_URL) {
      preloadFrames();
    } else {
      console.warn('[Rhion] Frame base URL not found — frame animation disabled');
    }

    // Canvas draw function
    let canvasCtx = null;
    const drawFrame = (index) => {
      if (!p3FrameCanvas || !frameImages[index]) return;
      const img = frameImages[index];
      if (!img.complete || img.naturalWidth === 0) return;

      // Canvas is full screen (100vw × 100vh)
      const wrapW = window.innerWidth;
      const wrapH = window.innerHeight;
      if (p3FrameCanvas.width !== wrapW || p3FrameCanvas.height !== wrapH) {
        p3FrameCanvas.width = wrapW;
        p3FrameCanvas.height = wrapH;
        canvasCtx = null; // reset after resize
      }

      if (!canvasCtx) canvasCtx = p3FrameCanvas.getContext('2d');
      if (!canvasCtx) return;

      const cw = p3FrameCanvas.width;
      const ch = p3FrameCanvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;

      // object-fit: cover — image fills full canvas
      const scale = Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;

      canvasCtx.clearRect(0, 0, cw, ch);
      canvasCtx.drawImage(img, dx, dy, dw, dh);
    };

    // Initialize canvas at full screen size and draw first frame
    const initCanvas = () => {
      if (p3FrameCanvas) {
        p3FrameCanvas.width = window.innerWidth;
        p3FrameCanvas.height = window.innerHeight;
        drawFrame(0);
      }
    };
    initCanvas();

    // Helper: Phase 1 3rd Letter 'o' metrics inside Logo.svg
    const getP1Metrics = () => {
      const vw = window.innerWidth;
      const svgW = p1LogoSvgWrap ? (p1LogoSvgWrap.getBoundingClientRect().width || p1LogoSvgWrap.offsetWidth || 340) : 340;
      const svgH = p1LogoSvgWrap ? (p1LogoSvgWrap.getBoundingClientRect().height || p1LogoSvgWrap.offsetHeight || 62) : 62;

      // Exact offset to place letter 'o' at dead-center
      let deltaX = 0.21968135 * svgW;

      // Micro-tuning specifically for <= 414px mobile viewports (iPhone 12 Pro 390px) to center perfectly
      if (vw <= 414) {
        deltaX -= 22;
      }

      const deltaY = (0.5 - 0.49733333) * svgH;

      return { deltaX, deltaY };
    };

    // Helper: Phase 2 Emblem relative shift to dead center
    const getP2ShiftX = () => {
      if (!p2Wordmark || !p2EmblemWrap || !p2Lockup) return 0;
      const wordmarkRect = p2Wordmark.getBoundingClientRect();
      const style = window.getComputedStyle(p2Lockup);
      const gap = parseFloat(style.columnGap || style.gap || '24') || 24;
      return (wordmarkRect.width + gap) / 2;
    };

    // Initial states for Phase 1 elements (Explicit xPercent/yPercent centering)
    gsap.set(p1LogoWrap, { xPercent: -50, yPercent: -50, x: 0, y: 0 });
    gsap.set(p1LogoSvgWrap, { transformOrigin: '28.031865% 49.733333%' });

    // Initial states for Phase 2 elements
    if (p2ElementSvgWrap) gsap.set(p2ElementSvgWrap, { opacity: 0, y: 0 });
    if (p2LensWrap) gsap.set(p2LensWrap, { opacity: 0, scale: 0.8 });

    // Initial states for Phase 3 elements
    if (p3TechnicalWrap) gsap.set(p3TechnicalWrap, { opacity: 0, x: () => (window.innerWidth <= 768 ? 0 : -60), y: () => (window.innerWidth <= 768 ? 25 : 0) });
    if (p3IntelligentWrap) gsap.set(p3IntelligentWrap, { opacity: 0, yPercent: () => (window.innerWidth <= 768 ? 0 : -50), x: () => (window.innerWidth <= 768 ? 0 : 40), y: () => (window.innerWidth <= 768 ? 25 : 0) });
    if (p3ShapeWrap) gsap.set(p3ShapeWrap, { opacity: 0, x: () => -window.innerWidth * 0.00 }); // <-- Gentle entry from ~12% left of center

    // Initial states for Phase 4 Hold Frame Text elements
    if (holdTextTop) gsap.set(holdTextTop, { opacity: 0, y: 30 });
    if (holdTextBottom) gsap.set(holdTextBottom, { opacity: 0, y: -30 });
    if (holdBulbLeft) gsap.set(holdBulbLeft, { opacity: 0, x: -35, y: 0, scale: 1 });
    if (holdBulbRight) gsap.set(holdBulbRight, { opacity: 0, x: 35, y: 0, scale: 1 });

    // Initial states for Phase 5 elements (5 Showcase Slides)
    [p5ImageWrap1, p5ImageWrap2, p5ImageWrap3, p5ImageWrap4, p5ImageWrap5].forEach((wrap) => {
      if (wrap) gsap.set(wrap, { opacity: 0, y: () => window.innerHeight * 0.08 });
    });
    [p5CenterWrap1, p5CenterWrap2, p5CenterWrap3, p5CenterWrap4, p5CenterWrap5].forEach((wrap) => {
      if (wrap) gsap.set(wrap, { opacity: 0, scale: 0.92 });
    });
    if (p5OutlineWrap) gsap.set(p5OutlineWrap, { opacity: 0, scale: 0.8 });

    // Master ScrollTrigger Timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 2.0, // Smooth cinematic catchup damping
        invalidateOnRefresh: true,
      }
    });

    // =============================================================
    // PHASE 1: STAGE 1 & 2 (0.00 -> 0.12) Logo.svg Zoom into 3rd letter 'o'
    // =============================================================
    // Other letters ('C', 'r', 'm', 'p', 't', 2nd 'o', 'n') stay visible longer and fade out smoothly (0.025 -> 0.065)
    if (p1LogoOthers) {
      tl.to(p1LogoOthers, {
        opacity: 0,
        duration: 0.032,
        ease: 'power2.in'
      }, 0.011);
    }

    // Logo shifts to dead-center together with initial zoom (0.00 -> 0.050 = 5%)
    tl.to(p1LogoWrap, {
      x: () => getP1Metrics().deltaX,
      y: () => getP1Metrics().deltaY,
      duration: 0.050,
      ease: 'power2.inOut'
    }, 0);

    // Full SVG zooms into 3rd letter 'o' (0.00 -> 0.11) - from 5% onwards it ONLY ZOOMS in place!
    tl.to(p1LogoSvgWrap, {
      scale: () => (window.innerWidth <= 768 ? 32 : 18),
      force3D: false,
      duration: 0.11,
      ease: 'power2.inOut'
    }, 0);

    // =============================================================
    // PHASE 1: STAGE 4 (0.12 -> 0.28) Sliding Marquee inside 'o' Portal (Frames 53-83)
    // =============================================================
    tl.to(p1MarqueeLayer, {
      opacity: 1,
      duration: 0.03,
      ease: 'power1.out'
    }, 0.12);

    tl.fromTo(p1MarqueeTrack, {
      x: () => window.innerWidth * 0.75
    }, {
      x: () => -window.innerWidth * 0.85,
      duration: 0.16,
      ease: 'none'
    }, 0.12);

    tl.to(p1MarqueeLayer, {
      opacity: 0,
      duration: 0.03,
      ease: 'power1.in'
    }, 0.27);

    // =============================================================
    // PHASE 1: STAGE 5 (0.27 -> 0.36) Hand Reveal & Magenta Shift on 'o'
    // =============================================================
    tl.to(p1SvgLetterO, {
      attr: { fill: '#ba1ab0' },
      duration: 0.08,
      ease: 'power2.inOut'
    }, 0.27);

    tl.to(p1HandWrap, {
      opacity: 1,
      scale: 1,
      duration: 0.08,
      ease: 'power2.out'
    }, 0.28);

    // =============================================================
    // PHASE 1: STAGE 6 (0.30 -> 0.38) 'o' Portal Expands Fullscreen
    // =============================================================
    tl.to(p1LogoSvgWrap, {
      scale: 65,
      force3D: false,
      duration: 0.07,
      ease: 'power2.inOut'
    }, 0.30);

    tl.to(p1SvgLetterO, {
      opacity: 0,
      duration: 0.05,
      ease: 'power2.in'
    }, 0.32);

    // =============================================================
    // PHASE 1: STAGE 7 (0.33 -> 0.38) Precision & Excellence Callouts Reveal
    // =============================================================
    tl.to(p1CalloutTop, {
      opacity: 1,
      x: 0,
      duration: 0.04,
      ease: 'power3.out'
    }, 0.33);

    tl.to(p1CalloutBottom, {
      opacity: 1,
      x: 0,
      duration: 0.04,
      ease: 'power3.out'
    }, 0.34);

    // =============================================================
    // SEAMLESS CROSSFADE: Phase 1 Fades Out (0.38 -> 0.42)
    // Both callouts and hand visual fade to opacity: 0 before Phase 2 starts
    // =============================================================
    tl.to([p1CalloutTop, p1CalloutBottom, p1HandWrap], {
      opacity: 0,
      duration: 0.04,
      ease: 'power2.in'
    }, 0.38);

    // =============================================================
    // PHASE 2: STEP 1 (0.42 -> 0.46) Launching + Lockup Appear
    // =============================================================
    tl.to(p2Container, {
      opacity: 1,
      duration: 0.04,
      ease: 'power1.out'
    }, 0.42);

    tl.to(p2HeadingWrap, {
      opacity: 1,
      y: 160,
      duration: 0.04,
      ease: 'power2.out'
    }, 0.43);

    tl.to(p2Lockup, {
      opacity: 1,
      duration: 0.04,
      ease: 'power2.out'
    }, 0.43);

    // =============================================================
    // PHASE 2: STEP 2 (0.46 -> 0.49) "Launching" Heading Fades Out
    // =============================================================
    tl.to(p2HeadingWrap, {
      opacity: 0,
      y: 100,
      duration: 0.03,
      ease: 'power2.in'
    }, 0.46);

    // =============================================================
    // PHASE 2: STEP 3 (0.46 -> 0.49) "Crompton Rhion" Wordmark Fades Out
    // =============================================================
    tl.to(p2Wordmark, {
      opacity: 0,
      x: 30,
      duration: 0.03,
      ease: 'power2.in'
    }, 0.46);

    // =============================================================
    // PHASE 2: STEP 4 (0.49 -> 0.52) Logomark Shifts to Exact Center
    // =============================================================
    tl.to(p2EmblemWrap, {
      x: () => getP2ShiftX(),
      y: 0,
      duration: 0.03,
      ease: 'power2.inOut'
    }, 0.49);

    // =============================================================
    // PHASE 2: STEP 5 (0.510 -> 0.560)
    // 1. Logomark rotates (132.5deg) + zooms
    // 2. As logo finishes rotation and starts fading out, outline slowly starts appearing
    // 3. Logo completely fades out -> Outline is 100% visible at lower position & HOLDS
    // =============================================================
    tl.to(p2EmblemScaler, {
      rotation: 132.5,
      scale: () => (window.innerWidth <= 768 ? 8.8 : 5.8), // <-- Desktop: Increased zoom (3.8 -> 5.8)
      y: () => (window.innerWidth <= 768 ? window.innerHeight * 0.24 : window.innerHeight * 0.12),
      duration: 0.035,
      ease: 'power2.inOut'
    }, 0.510);

    tl.to(p2Swirls, {
      opacity: 0,
      duration: 0.020,
      ease: 'power1.inOut'
    }, 0.530);

    tl.to(p2LogomarkSvg, {
      opacity: 0,
      duration: 0.020,
      ease: 'power2.in'
    }, 0.530);

    // As logo starts fading, outline slowly starts appearing at lower position (y: 0) and holds
    if (p2ElementSvgWrap) {
      tl.to(p2ElementSvgWrap, {
        opacity: 1,
        y: 0,
        duration: 0.025,
        ease: 'power2.out'
      }, 0.535);
    }

    // =============================================================
    // PHASE 2: STEP 6 (0.560 -> 0.610)
    // Logo is now 100% gone.
    // Outline holds in place at lower position.
    // "Precision in every element" text reveals over outline, holds, then fades out.
    // =============================================================
    if (p2ElementHeadingWrap) {
      tl.to(p2ElementHeadingWrap, {
        opacity: 1,
        y: 0,
        duration: 0.025,
        ease: 'power2.out'
      }, 0.560);

      tl.to(p2ElementHeadingWrap, {
        opacity: 0,
        y: -25,
        duration: 0.020,
        ease: 'power2.in'
      }, 0.590);
    }

    // =============================================================
    // PHASE 2: STEP 7 (0.615 -> 0.655)
    // Text has faded out. Outline SVG now travels upward and holds at top position.
    // =============================================================
    if (p2ElementSvgWrap) {
      tl.to(p2ElementSvgWrap, {
        y: () => (window.innerWidth <= 768 ? -window.innerHeight * 0.72 : -window.innerHeight * 1.15),
        duration: 0.035,
        ease: 'power2.out'
      }, 0.615);
    }

    // =============================================================
    // PHASE 2: STEP 8 (0.645 -> 0.710)
    // Outline is now at top position and HOLDS.
    // Mask group.png (purple glowing lens) appears inside the bottom bulb and HOLDS!
    // Stays completely still on screen from 0.670 to 0.710 before rotation begins.
    // =============================================================
    if (p2LensWrap) {
      tl.to(p2LensWrap, {
        opacity: 1,
        scale: 1,
        duration: 0.025,
        ease: 'back.out(1.4)'
      }, 0.645);
    }

    // =============================================================
    // PHASE 3: STAGE 1 (0.710 -> 0.760)
    // 1. Exactly at 0.710: Outline starts rotating 90° right
    // 2. Exactly at 0.710: Mask image starts fading out immediately
    // 3. shape.png enters from LEFT -> RIGHT (x: 0.52 * vw) during rotation
    // 4. Left Text "Technical mastery" reveals from left during rotation
    // =============================================================
    if (p2LensWrap) {
      tl.to(p2LensWrap, {
        opacity: 0,
        duration: 0.010,
        ease: 'power3.in'
      }, 0.710);
    }

    if (p2ElementSvgWrap) {
      tl.to(p2ElementSvgWrap, {
        y: () => (window.innerWidth <= 768 ? -window.innerHeight * 0.68 : -window.innerHeight * 0.92),
        duration: 0.030,
        ease: 'power2.in'
      }, 0.710);

      tl.to(p2ElementRotator, {
        rotation: 90,
        x: () => (window.innerWidth <= 768 ? 0 : window.innerWidth * 0.20),
        duration: 0.045,
        ease: 'power2.out'
      }, 0.710);

      tl.to(p2ElementSvgWrap, {
        y: () => (window.innerWidth <= 768 ? -window.innerHeight * 0.72 : -window.innerHeight * 1.22), // <-- Mobile: Jada niche drop nahi karega
        duration: 0.035,
        ease: 'back.out(1.7)'
      }, 0.725);
    }

    if (p3TechnicalWrap) {
      tl.to(p3TechnicalWrap, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 0.040,
        ease: 'power3.out'
      }, 0.715);
    }

    if (p3ShapeWrap) {
      tl.to(p3ShapeWrap, {
        opacity: 1,
        x: () => (window.innerWidth <= 768 ? window.innerWidth * 0.18 : window.innerWidth * 0.44), // <-- Halka sa kam right shift
        duration: 0.050,
        ease: 'power2.out'
      }, 0.715);
    }

    // =============================================================
    // PHASE 3: STAGE 2 (0.760 -> 0.815)
    // 1. Outline & "Technical mastery" fade out completely
    // 2. shape.png shifts further right on mobile
    // 3. "Intelligent design" reveals delayed on the right
    // =============================================================
    if (p2ElementSvgWrap) {
      tl.to(p2ElementSvgWrap, {
        opacity: 0,
        duration: 0.025,
        ease: 'power2.in'
      }, 0.715);
    }

    if (p3TechnicalWrap) {
      tl.to(p3TechnicalWrap, {
        opacity: 0,
        x: () => (window.innerWidth <= 768 ? 0 : -80),
        y: () => (window.innerWidth <= 768 ? -25 : 0),
        duration: 0.025,
        ease: 'power2.in'
      }, 0.740); // <-- Fadeout starts earlier as shape shifts
    }

    if (p3ShapeWrap) {
      tl.to(p3ShapeWrap, {
        opacity: 1,
        x: () => (window.innerWidth <= 768 ? -window.innerWidth * 0.70 : -window.innerWidth * 0.49), // <-- Desktop Left Shift (0.44 = kam left)
        scale: () => (window.innerWidth <= 768 ? 1 : 1.5), // <-- Scale maintain (1.3 on desktop so size doesn't decrease)
        duration: 0.045,
        ease: 'power2.out'
      }, 0.760);
    }

    if (p3IntelligentWrap) {
      tl.to(p3IntelligentWrap, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 0.035,
        ease: 'power3.out'
      }, 0.785);
    }

    // =============================================================
    // PHASE 3: STAGE 3A (0.815 -> 0.845) - SHAPE PANS TO DEAD-CENTER
    // 1. "Intelligent design" text fades out early as shape centers
    // 2. shape.png pans smoothly to DEAD-CENTER (x: 0) & scales down on mobile
    // =============================================================
    if (p3IntelligentWrap) {
      tl.to(p3IntelligentWrap, {
        opacity: 0,
        x: () => (window.innerWidth <= 768 ? 0 : 40),
        y: () => (window.innerWidth <= 768 ? -25 : 0),
        duration: 0.025,
        ease: 'power2.in'
      }, 0.800); // <-- Fadeout starts earlier as shape moves to center
    }

    if (p3ShapeWrap) {
      tl.to(p3ShapeWrap, {
        opacity: 1,
        x: () => (window.innerWidth <= 768 ? -window.innerWidth * 0.28 : 0), // <-- Exact Dead-Center (78% - 28% = 50%)
        scale: () => (window.innerWidth <= 768 ? 0.60 : 1.5), // <-- Halka sa increase size (0.60 on mobile)
        duration: 0.030,
        ease: 'power2.inOut'
      }, 0.810);
    }

    // =============================================================
    // PHASE 3: STAGE 3B & 3C (0.840 -> 0.855) - CROSSFADE TO CANVAS FRAME 543
    // =============================================================
    if (FRAME_BASE_URL && p3ShapeImg) {
      tl.to(p3ShapeImg, {
        opacity: 0,
        duration: 0.015,
        ease: 'power1.in'
      }, 0.840);
    }

    if (FRAME_BASE_URL && p3FrameCanvas) {
      tl.to(p3FrameCanvas, {
        opacity: 1,
        duration: 0.015,
        ease: 'power1.out'
      }, 0.840);
    }

    // =============================================================
    // PHASE 3: STAGE 3D (0.855 -> 0.890) - SCROLL-DRIVEN FRAME ANIMATION (543 -> 602)
    // Pure frames play through first
    // =============================================================
    const frameObj = { frame: 0 };
    if (p3FrameCanvas && FRAME_BASE_URL) {
      tl.to(frameObj, {
        frame: TOTAL_FRAMES,
        ease: 'none',
        duration: 0.035,
        onUpdate: () => {
          drawFrame(Math.round(frameObj.frame));
        }
      }, 0.855);
    }

    // =============================================================
    // PHASE 3: STAGE 3E (0.890 -> 0.915) - SOLID HOLD OF LAST FRAME (602)
    // Final 3D rendered loop sits completely still and holds before texts appear!
    // =============================================================

    // =============================================================
    // PHASE 4: SEQUENTIAL TEXT REVEALS OVER FINAL HOLD FRAME (0.915 -> 0.940)
    // =============================================================

    // --- Step 1: Top Center Text ("The finest expression of") (0.915 -> 0.925) ---
    if (holdTextTop) {
      tl.to(holdTextTop, {
        opacity: 1,
        y: 0,
        duration: 0.006,
        ease: 'power2.out'
      }, 0.915);

      tl.to(holdTextTop, {
        opacity: 0,
        y: -20,
        duration: 0.005,
        ease: 'power2.in'
      }, 0.921);
    }

    // --- Step 2: Bottom Center Text ("new generation home experiences.") (0.925 -> 0.935) ---
    if (holdTextBottom) {
      tl.to(holdTextBottom, {
        opacity: 1,
        y: 0,
        duration: 0.006,
        ease: 'power2.out'
      }, 0.925);

      tl.to(holdTextBottom, {
        opacity: 0,
        y: 20,
        duration: 0.005,
        ease: 'power2.in'
      }, 0.931);
    }

    // --- Step 3: Dual Bulb Texts ("Prowess inside." & "Poise outside.") (0.935 -> 0.940) ---
    if (holdBulbLeft) {
      tl.to(holdBulbLeft, {
        opacity: 1,
        x: 0,
        duration: 0.006,
        ease: 'power2.out'
      }, 0.935);
    }

    if (holdBulbRight) {
      tl.to(holdBulbRight, {
        opacity: 1,
        x: 0,
        duration: 0.006,
        ease: 'power2.out'
      }, 0.935);
    }

    // =============================================================
    // PHASE 5: 5 SHOWCASE SLIDES WITH AMPLE HOLDS (0.940 -> 1.000)
    // Each slide gets ~1.2% scroll space with hold time
    // =============================================================

    // --- Transition into Slide 1: Engineering Excellence (0.940) ---
    if (p3FrameCanvas) {
      tl.to(p3FrameCanvas, {
        opacity: 0,
        duration: 0.006,
        ease: 'power2.inOut'
      }, 0.940);
    }

    // Slide "Prowess inside." down to image level & KEEP IT THERE
    if (holdBulbLeft) {
      tl.to(holdBulbLeft, {
        x: () => (window.innerWidth <= 768 ? -window.innerWidth * 0.02 : -window.innerWidth * 0.08),
        y: () => (window.innerWidth <= 768 ? window.innerHeight * 0.16 : window.innerHeight * 0.35),
        scale: () => (window.innerWidth <= 768 ? 0.78 : 0.85),
        duration: 0.006,
        ease: 'power2.inOut'
      }, 0.940);
    }

    // Slide "Poise outside." down to image level & KEEP IT THERE
    if (holdBulbRight) {
      tl.to(holdBulbRight, {
        x: () => (window.innerWidth <= 768 ? window.innerWidth * 0.02 : window.innerWidth * 0.08),
        y: () => (window.innerWidth <= 768 ? window.innerHeight * 0.16 : window.innerHeight * 0.35),
        scale: () => (window.innerWidth <= 768 ? 0.78 : 0.85),
        duration: 0.006,
        ease: 'power2.inOut'
      }, 0.940);
    }

    // Small glowing outline SVG reveals at bottom center & KEEPS SHOWING
    if (p5OutlineWrap) {
      tl.to(p5OutlineWrap, {
        opacity: 1,
        scale: 1,
        duration: 0.006,
        ease: 'back.out(1.4)'
      }, 0.941);
    }

    // Slide 1 Image (Engineering.png) rises from bottom
    if (p5ImageWrap1) {
      tl.to(p5ImageWrap1, {
        opacity: 1,
        y: 0,
        duration: 0.005,
        ease: 'power2.out'
      }, 0.940);
    }

    // Slide 1 Heading ("Engineering Excellence") — appears then HOLDS
    if (p5CenterWrap1) {
      tl.to(p5CenterWrap1, {
        opacity: 1,
        scale: 1,
        duration: 0.005,
        ease: 'power3.out'
      }, 0.942);
    }

    // --- Slide 1 text fades out slowly → Transition to Slide 2 (0.952) ---
    if (p5CenterWrap1) {
      tl.to(p5CenterWrap1, {
        opacity: 0,
        y: -25,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.952);
    }

    if (p5ImageWrap2) {
      tl.to(p5ImageWrap2, {
        opacity: 1,
        y: 0,
        duration: 0.005,
        ease: 'power2.out'
      }, 0.953);
    }

    // Slide 2 Heading ("Design Intelligence") — appears then HOLDS
    if (p5CenterWrap2) {
      tl.to(p5CenterWrap2, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.005,
        ease: 'power3.out'
      }, 0.955);
    }

    // --- Slide 2 text fades out slowly → Transition to Slide 3 (0.965) ---
    if (p5CenterWrap2) {
      tl.to(p5CenterWrap2, {
        opacity: 0,
        y: -25,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.965);
    }

    if (p5ImageWrap3) {
      tl.to(p5ImageWrap3, {
        opacity: 1,
        y: 0,
        duration: 0.005,
        ease: 'power2.out'
      }, 0.966);
    }

    // Slide 3 Heading ("Elevated Living") — appears then HOLDS
    if (p5CenterWrap3) {
      tl.to(p5CenterWrap3, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.005,
        ease: 'power3.out'
      }, 0.968);
    }

    // --- Slide 3 text fades out slowly → Transition to Slide 4 (0.977) ---
    if (p5CenterWrap3) {
      tl.to(p5CenterWrap3, {
        opacity: 0,
        y: -25,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.977);
    }

    if (p5ImageWrap4) {
      tl.to(p5ImageWrap4, {
        opacity: 1,
        y: 0,
        duration: 0.005,
        ease: 'power2.out'
      }, 0.978);
    }

    // Slide 4 Heading ("Lifestyle Architecture") — appears then HOLDS
    if (p5CenterWrap4) {
      tl.to(p5CenterWrap4, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.005,
        ease: 'power3.out'
      }, 0.980);
    }

    // --- Transition to Slide 5: The World of Rhion (0.988) ---
    // 1. Slide 4 text & image fade out
    if (p5CenterWrap4) {
      tl.to(p5CenterWrap4, {
        opacity: 0,
        y: -25,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.988);
    }

    // 2. Corner texts ("Prowess inside.", "Poise outside.") fade out
    if (holdBulbLeft) {
      tl.to(holdBulbLeft, {
        opacity: 0,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.988);
    }

    if (holdBulbRight) {
      tl.to(holdBulbRight, {
        opacity: 0,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.988);
    }

    // 3. Small bottom outline SVG fades out
    if (p5OutlineWrap) {
      tl.to(p5OutlineWrap, {
        opacity: 0,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.988);
    }

    // 4. Slide 5 Image (World Of Rhion BG.png) rises and reveals
    if (p5ImageWrap5) {
      tl.to(p5ImageWrap5, {
        opacity: 1,
        y: 0,
        duration: 0.005,
        ease: 'power2.out'
      }, 0.989);
    }

    // 5. Slide 5 Heading ("The world of Rhion brings to you") reveals dead-center — HOLDS
    if (p5CenterWrap5) {
      tl.to(p5CenterWrap5, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.005,
        ease: 'power3.out'
      }, 0.991);
    }

    // =============================================================
    // SLIDE 5 EXIT: Fade out everything cleanly (0.996 -> 1.000)
    // =============================================================

    // Fade out all previous slide images (1-4) so none peek through
    [p5ImageWrap1, p5ImageWrap2, p5ImageWrap3, p5ImageWrap4].forEach((wrap) => {
      if (wrap) {
        tl.to(wrap, { opacity: 0, duration: 0.004, ease: 'power2.in' }, 0.996);
      }
    });

    // Fade out Slide 5 text
    if (p5CenterWrap5) {
      tl.to(p5CenterWrap5, {
        opacity: 0,
        y: -30,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.996);
    }

    // Fade out Slide 5 image
    if (p5ImageWrap5) {
      tl.to(p5ImageWrap5, {
        opacity: 0,
        duration: 0.004,
        ease: 'power2.in'
      }, 0.996);
    }

    // Next Section (Scroll Overlay) — fixed crossfade at end of story
    const nextSection = document.querySelector('.rihon-scroll-overlay');
    if (nextSection) {
      // Position fixed so it appears IN PLACE over the story (not scrolling)
      nextSection.style.cssText += ';position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;opacity:0;pointer-events:none;';

      // Fade IN the overlay inside the timeline (crossfade with Slide 5 exit)
      tl.to(nextSection, {
        opacity: 1,
        duration: 0.004,
        ease: 'power2.out'
      }, 0.996);

      // When story section fully exits viewport, convert overlay back to normal flow
      // so footer and subsequent sections scroll naturally
      let hasUnfixed = false;
      ScrollTrigger.create({
        trigger: section,
        start: 'bottom bottom',
        end: 'bottom top',
        onLeave: () => {
          if (!hasUnfixed) {
            hasUnfixed = true;
            nextSection.style.cssText = nextSection.style.cssText
              .replace(/position\s*:\s*fixed\s*;?/gi, 'position:relative;')
              .replace(/top\s*:\s*0\s*;?/gi, '')
              .replace(/left\s*:\s*0\s*;?/gi, '')
              .replace(/width\s*:\s*100vw\s*;?/gi, 'width:100%;')
              .replace(/z-index\s*:\s*9999\s*;?/gi, 'z-index:10;');
            nextSection.style.pointerEvents = 'auto';
            ScrollTrigger.refresh();
          }
        },
        onEnterBack: () => {
          if (hasUnfixed) {
            hasUnfixed = false;
            nextSection.style.cssText += ';position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999;';
          }
        }
      });
    }


    // Handle Window Resize
    const handleResize = () => {
      p1M = getP1Metrics();
      if (p1LogoSvgWrap) gsap.set(p1LogoSvgWrap, { transformOrigin: `${p1M.originX}% ${p1M.originY}%` });
      // Reset canvas dimensions on resize
      if (p3FrameCanvas) {
        p3FrameCanvas.width = window.innerWidth;
        p3FrameCanvas.height = window.innerHeight;
        canvasCtx = null;
      }
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', handleResize, { passive: true });
  };

  // Boot Master Engine
  const start = async () => {
    try {
      await ensureGsap();
      await initLenisSmoothScroll();
      document.querySelectorAll('[data-rhion-story-master]').forEach(initMasterStory);
    } catch (e) {
      console.warn('[Rhion Story Master] Engine boot warning:', e);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Shopify Theme Editor Reload Support
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector?.('[data-rhion-story-master]') || (e.target.matches?.('[data-rhion-story-master]') ? e.target : null);
    if (el) {
      ensureGsap().then(() => {
        initLenisSmoothScroll();
        initMasterStory(el);
      });
    }
  });

})();
